/**
 * Share the whole meeting (the header's Share button; a transcript selection's "Share clip" shares a part). The link is
 * the meeting's public page on this site's own domain (https://<site host>/meeting/<id>, app/site.ts), which unfurls with
 * the page's title, description and video still (worker/src/city-site.js, seo.js).
 *
 * Where the browser has a share sheet (phones), the button opens it. Elsewhere it opens a small menu: Copy link, the
 * link from the moment you're at (?t=<seconds>, which the page plays from: main.tsx startAt) once you're partway in,
 * and plain share links for X, Facebook, LinkedIn, Bluesky and email. No third-party scripts. Escape closes the menu
 * and puts focus back on the button; arrow keys move through it.
 */
import { useEffect, useRef, useState } from "react";
import { Check, Share2 } from "lucide-react";
import { meetingStore, useMeeting } from "../../stores/meetingStore";
import { clock, meetingWhen } from "../../lib/format";
import { site } from "../../app/site";
import { track } from "../../lib/analytics";

const enc = encodeURIComponent;

/**
 * The meeting's public address on this site's canonical host, from the dashboard too (never /dashboard/meetings/<id>,
 * which asks whoever opens it to sign in): ?t= a moment in seconds, or a clip's range as ShareClip.tsx writes it.
 */
export const shareUrl = (id: string, t?: number | string) =>
  `https://${site.host}/meeting/${enc(id)}${t ? `?t=${typeof t === "number" ? Math.floor(t) : t}` : ""}`;

/** Where each service takes a link (their plain share intents). */
export function shareTargets(url: string, title: string, text: string) {
  return [
    { id: "x", label: "X", href: `https://x.com/intent/post?text=${enc(title)}&url=${enc(url)}` },
    { id: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { id: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { id: "bluesky", label: "Bluesky", href: `https://bsky.app/intent/compose?text=${enc(`${title} ${url}`)}` },
    { id: "email", label: "Email", href: `mailto:?subject=${enc(title)}&body=${enc(`${text}\n\n${url}`)}` },
  ];
}

export function ShareMeeting() {
  const meeting = useMeeting((s) => s.meeting)!;
  const summary = useMeeting((s) => s.report?.summary ?? "");
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [at, setAt] = useState(0);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const title = `${meeting.body} meeting, ${meetingWhen(meeting.startsAt).split(" · ")[0]}`;
  const text = summary ? summary.split(/(?<=[.!?])\s+/)[0] : `${meeting.body} · ${meetingWhen(meeting.startsAt)} · ${site.name}`;
  const url = shareUrl(meeting.id);

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => { if (!menu.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); button.current?.focus(); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const items = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
      const i = items.indexOf(document.activeElement as HTMLElement);
      items[(i + (e.key === "ArrowDown" ? 1 : items.length - 1)) % items.length]?.focus();
    };
    document.addEventListener("pointerdown", close, true);
    addEventListener("keydown", key);
    requestAnimationFrame(() => menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus());
    return () => { document.removeEventListener("pointerdown", close, true); removeEventListener("keydown", key); };
  }, [open]);

  const onClick = () => {
    if (typeof navigator.share === "function" && matchMedia("(pointer: coarse)").matches) {
      void navigator.share({ title, text, url }).catch(() => {});
      track("share", { method: "native", content_type: "meeting", item_id: meeting.id });
      return;
    }
    const t = meetingStore.getState().currentTime, duration = meeting.video?.duration ?? 0;
    setAt(t > 5 && (!duration || t < duration - 5) ? Math.floor(t) : 0);
    setOpen((o) => !o);
  };
  const copy = (link: string, which: string) => void navigator.clipboard?.writeText(link).then(() => {
    track("share", { method: which === "at" ? "copy_at_time" : "copy", content_type: "meeting", item_id: meeting.id });
    setCopied(which);
    setTimeout(() => setCopied(null), 1500);
  });

  return (
    <span className="share-meeting">
      <button ref={button} className="btn btn--secondary" aria-haspopup="menu" aria-expanded={open} aria-label="Share this meeting" onClick={onClick}>
        <Share2 size={14} />Share
      </button>
      {open && (
        <div ref={menu} className="dd-pop share-menu" role="menu" aria-label="Share this meeting">
          <button role="menuitem" className="dd-option" onClick={() => copy(url, "link")}>
            {copied === "link" ? <><Check size={14} />Copied</> : "Copy link"}
          </button>
          {at > 0 && (
            <button role="menuitem" className="dd-option" onClick={() => copy(shareUrl(meeting.id, at), "at")}>
              {copied === "at" ? <><Check size={14} />Copied</> : `Share from ${clock(at)}`}
            </button>
          )}
          {shareTargets(url, title, text).map((x) => (
            <a key={x.id} role="menuitem" className="dd-option" href={x.href} target={x.id === "email" ? undefined : "_blank"} rel="noopener noreferrer"
              onClick={() => { track("share", { method: x.id, content_type: "meeting", item_id: meeting.id }); setOpen(false); }}>{x.label}</a>
          ))}
        </div>
      )}
    </span>
  );
}
